import { bind, h, signal, tag } from "omi";
import Dropzone, { DropzoneFile } from "dropzone";

import { NWDropzone } from "@/components/atoms/nw-dropzone";
import { fireEvent, ValidationEvents } from "@/components/validations/events";
import { UploadValidation } from "@/components/validations/models";
import { ValidationBase } from "@/components/validations/validationBase";
import { AuthEvents } from "@/components/nw-auth-manager";
import { ValidationStatus, ValidationType } from "@/api";
import { Store } from "@/store";
import { tailwind } from "@/tailwind";

import { EditAnswer } from "../common/editAnswer";
import baseStyles from "../baseStyles.css?inline";

export type Props = {
  title: string;
  description: string;
  disabled?: boolean;
  optional?: boolean;
};

@tag("nw-validation-upload")
export class NWValidationUpload extends ValidationBase<UploadValidation, Props> {
  static css = [tailwind, baseStyles];

  static propTypes = {
    title: String,
    description: String,
    disabled: Boolean,
    optional: Boolean,
  };

  file = signal<DropzoneFile | undefined>(undefined);
  private hydrated = signal<boolean>(false);

  // ready() {
  //   this.addEventListener("focusout", this.saveOnFocusOut);
  // }
  //
  // uninstall() {
  //   this.removeEventListener("focusout", this.saveOnFocusOut);
  // }
  //
  // @bind
  // private saveOnFocusOut() {
  //   const isDisabled = !this.isEditable() || this.props.disabled;
  //   if (isDisabled) {
  //     return;
  //   }
  //   this.save();
  // }

  @bind
  async generateSignedUrl() {
    if (!Store.user.isLoggedIn.value || !this.isEditable()) {
      return undefined;
    }

    const response = await fetch(`/uploads/generate-signed-url?fileName=${encodeURIComponent(this.getFileKey())}`);
    if (!response.ok) {
      throw new Error("Unable to create signed url");
    }

    const resJson = await response.json();
    return resJson.url;
  }

  private getFileKey(): string {
    return `${Store.project.id.value}_${this.id}`;
  }

  @bind
  isFilled(): boolean {
    return this.file.value?.status === Dropzone.SUCCESS;
  }

  @bind
  async processInternal(): Promise<ValidationStatus> {
    const file = this.file.value;
    const hasSucceeded = file && file.status === Dropzone.SUCCESS;

    if (hasSucceeded) {
      return ValidationStatus.Complete;
    }

    return ValidationStatus.Incorrect;
  }

  async hydrate(validation: UploadValidation | undefined) {
    this.status.value = validation?.status || this.status.value;

    if (validation?.file && validation?.file.name) {
      this.file.value = this.createFileFromHydrationData(validation.file);
      this.hydrated.value = true;
    }
  }

  private createFileFromHydrationData(file: UploadValidation["file"]): DropzoneFile | undefined {
    if (file == null || file.name == null || file.storageName == null) {
      return undefined;
    }

    // We only have access to a few attributes on hydration.
    return {
      name: file.name,
      status: Dropzone.SUCCESS,
      dataURL: `/uploads/${file.storageName}`,
    } as DropzoneFile;
  }

  @bind
  toDTO(): UploadValidation {
    const file = this.file.peek();

    if (!file) {
      return {
        id: this.id,
        status: this.status.value,
        type: ValidationType.File,
      };
    }

    return {
      id: this.id,
      status: this.status.value,
      type: ValidationType.File,
      file: {
        name: file.name,
        storageName: this.getFileKey(),
      },
    };
  }

  @bind
  private queuedFileForUpload() {
    if (this.isOptional()) {
      fireEvent(ValidationEvents.OPTIONAL_FOCUSED, { id: this.id });
    }

    if (this.status.value === ValidationStatus.InReview) {
      this.status.value = ValidationStatus.Incomplete;
      fireEvent(ValidationEvents.IN_REVIEW_FOCUSED, { id: this.id });
    }

    if (Store.user.isPro() && this.status.value === ValidationStatus.Complete) {
      this.status.value = ValidationStatus.Incomplete;
      fireEvent(ValidationEvents.EDITED, { id: this.id });
      if (Store.project.id.value != null) {
        import("@/services/analyticsService").then(({ useAnalyticsService }) => {
          useAnalyticsService().trackValidationEdited(Store.project.id.value!, this.id);
        });
      }
    }
  }

  @bind
  private onUploadSuccess(file: DropzoneFile | undefined) {
    this.file.value = file;

    if (file && file.status === Dropzone.SUCCESS) {
      this.hydrated.value = false;
      this.save();
    }
  }

  @bind
  private onUploadError() {
    this.file.value = undefined;
    this.status.value = ValidationStatus.Incomplete;
  }

  @bind
  private handleLogin(e: Event) {
    e.preventDefault();
    this.triggerLogin();
  }

  private triggerLogin() {
    if (!Store.user.isLoggedIn.value) {
      const loginEvent = new Event(AuthEvents.LoginClicked);
      dispatchEvent(loginEvent);
    }
  }

  render({ title, description, disabled = false }: Props) {
    const isDisabled = !this.isEditable() || disabled;
    const hasFileContent = !!this.file.value;

    return (
      <div>
        <div className="space-y-4 mb-10">
          <h3 className="font-bold text-2xl text-primary">{title}</h3>
          <p unsafeHTML={{ html: description }}></p>
        </div>

        <div>
          <NWDropzone
            getUploadUrl={this.generateSignedUrl}
            onUploadSuccess={this.onUploadSuccess}
            onUploadError={this.onUploadError}
            disabled={isDisabled}
            hasFile={hasFileContent}
            onAddFile={this.queuedFileForUpload}
            fileName={this.file.value?.name}
            accept=".jpg,.png"
            subtext="PNG or JPG (max. 10MB)"
            initialFile={this.file.value}
            onFocus={this.handleLogin}
            maxSize={10}
            dropzoneClassNames="bg-white"
            disableDragAndDrop={isDisabled}
            withTrashIconForErrors
          />

          {this.status.value === ValidationStatus.Complete && (
            <div class="my-1.5">
              <EditAnswer onClick={() => this.dropzone.element.click()} />
            </div>
          )}
        </div>
      </div>
    );
  }
}
